import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";

const ARTERRA_ORG_ID = "5eb659a7fd1e82a9df674eee";
const SB_DHALIWAL_ORG_ID = "6893816617fb4d000713f989";

/** `minutes` | `hours` — label copy comes from i18n (`preset-label-minutes` / `preset-label-hours`). */
export type PresetTimeUnitKey = "minutes" | "hours";

export type DurationPreset = { amount: number; value: number; unit: PresetTimeUnitKey };
export type StartTimePreset = { amount: number; offsetMs: number; unit: PresetTimeUnitKey };
export type IrrigationPresets = { duration: DurationPreset[]; startTime: StartTimePreset[] };
export type OrgPresets = { defaults: IrrigationPresets; zoneOverrides?: Record<string, IrrigationPresets> };
/**
 * Default presets for create irrigation (event)
 */
const DEFAULT_PRESETS: IrrigationPresets = {
    duration: [
        { amount: 20, value: 20 * minuteMs, unit: "minutes" },
        { amount: 35, value: 35 * minuteMs, unit: "minutes" },
        { amount: 45, value: 45 * minuteMs, unit: "minutes" },
    ],
    startTime: [
        { amount: 15, offsetMs: 15 * minuteMs, unit: "minutes" },
        { amount: 30, offsetMs: 30 * minuteMs, unit: "minutes" },
        { amount: 60, offsetMs: hourMs, unit: "minutes" },
    ],
};

/**
 * Presets for create irrigation (event) for specific organizations
 * Per-zone overrides take precedence over org-level presets.
 * Add zone node_ids here when a specific zone needs different presets than its org.
 */
const ORG_PRESETS: Record<string, OrgPresets> = {
    [ARTERRA_ORG_ID]: {
        defaults: {
            duration: [
                { amount: 2, value: 2 * hourMs, unit: "hours" },
                { amount: 3, value: 3 * hourMs, unit: "hours" },
                { amount: 4, value: 4 * hourMs, unit: "hours" },
                { amount: 6, value: 6 * hourMs, unit: "hours" },
            ],
            startTime: [
                { amount: 2, offsetMs: 2 * hourMs, unit: "hours" },
                { amount: 4, offsetMs: 4 * hourMs, unit: "hours" },
                { amount: 6, offsetMs: 6 * hourMs, unit: "hours" },
            ],
        },
    },
    [SB_DHALIWAL_ORG_ID]: {
        defaults: {
            duration: [
                { amount: 4, value: 4 * hourMs, unit: "hours" },
                { amount: 6, value: 6 * hourMs, unit: "hours" },
                { amount: 8, value: 8 * hourMs, unit: "hours" },
                { amount: 10, value: 10 * hourMs, unit: "hours" },
            ],
            startTime: [
                { amount: 2, offsetMs: 2 * hourMs, unit: "hours" },
                { amount: 4, offsetMs: 4 * hourMs, unit: "hours" },
                { amount: 6, offsetMs: 6 * hourMs, unit: "hours" },
            ],
        },
        //  "zone-overrides": {
        //     "zone-node-id-here": {
        //         duration: [...],
        //         startTime: [...],
        //     }
        // }
    },
};

/**
 * Get the presets for the given zone id. using cascade logic:
 * Zone-specific -> Org-specific -> Default
 */
export function useIrrigationPresets(zoneId: string): { presets: IrrigationPresets } {
    const orgId = SessionHandler.currentUserObject?.dbID;
    const orgPresets = orgId !== undefined ? ORG_PRESETS[orgId] : undefined;
    const zoneOverrides = orgPresets?.zoneOverrides?.[zoneId];

    return { presets: zoneOverrides ?? orgPresets?.defaults ?? DEFAULT_PRESETS };
}
